import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useT } from '@/i18n';
import { toast } from '@/stores/toast.store';
import { apiCall, ApiError } from '@/lib/api-client';
import { clientsApi, type ClientProfile } from './clients.api';
import { inboundClaimErrorKey } from './claimInbound.errors';

/**
 * State-aware action button for a patient profile. Renders the right CTA
 * for the viewing role + current assignment state:
 *
 *  - agent owning the active assignment → "Ir a pre-call" (with preflight
 *    refetch — if the assignment was revoked by IdleSessionReaper between
 *    the profile read and the click, surface a clear toast instead of a
 *    Bad Request on /pre-call). Cluster A followup — A-1 + A-2.
 *  - agent + foreign active assignment → "Solicitar reasignación".
 *  - agent + no active assignment → "El miembro está llamando — atender
 *    ahora" (S5, crea la asignación y abre pre-call) + "Solicitar asignación"
 *    como salida cuando la reja del backend lo rechaza. H5: el botón exige el
 *    número DESDE EL QUE llama el miembro — el servidor lo coteja contra el
 *    registro (403) y cuenta los reclamos del día (429).
 *  - admin → link to /admin/reassign (existing UI handles both assign/reassign).
 *  - supervisor / uploader → no action button.
 */
export function AssignmentAction(props: {
  profile: ClientProfile;
  currentUser: { id: string; role: string };
}): JSX.Element | null {
  const { profile, currentUser } = props;
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const aa = profile.activeAssignment;

  // H5 — el número DESDE EL QUE llama el miembro. El servidor lo coteja contra
  // el registro y sin él no hay reclamo. Se pide acá, con el miembro en la
  // línea, porque es el único momento en que alguien lo sabe.
  const [inboundPhone, setInboundPhone] = useState('');
  const telefonoUsable = (inboundPhone.match(/\d/g) ?? []).length >= 10;

  const requestReassign = useMutation({
    mutationFn: () => clientsApi.requestReassign(profile.id),
    onSuccess: () => {
      toast.success(
        t('clientProfile.requestReassign.successTitle'),
        t('clientProfile.requestReassign.successBody'),
      );
    },
    onError: (err) => {
      toast.error(
        t('clientProfile.requestReassign.errorTitle'),
        (err as Error).message,
      );
    },
  });

  // S5 — «El miembro está llamando». Crea la asignación y la reclama en un
  // paso; la respuesta es la misma de take-next, así que se navega directo a
  // pre-call. Los rechazos de la reja traen un `code` y se traducen a algo
  // accionable: el crudo del server está en inglés y no dice qué hacer.
  const claimInbound = useMutation({
    mutationFn: () => clientsApi.claimInbound(profile.id, inboundPhone),
    onSuccess: (claim) => {
      queryClient.invalidateQueries({ queryKey: ['clients.profile', profile.id] });
      navigate(`/pre-call/${claim.assignmentId}`);
    },
    onError: (err) => {
      const key = inboundClaimErrorKey(err);
      toast.error(
        t('clientProfile.claimInbound.errorTitle'),
        key ? t(key) : (err as Error).message,
      );
    },
  });

  const goPreCall = async (): Promise<void> => {
    if (!aa) return;
    try {
      // Preflight: refresh the active claim from the backend before navigating.
      // If the assignment was revoked (IdleSessionReaper, released by another
      // tab, etc) the endpoint returns 404. We catch and surface a clear toast.
      await apiCall(`/agents/me/queue/${encodeURIComponent(aa.id)}/details`);
      navigate(`/pre-call/${aa.id}`);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 410)) {
        toast.error(
          t('clientProfile.goToPreCall.staleTitle'),
          t('clientProfile.goToPreCall.staleBody'),
        );
        queryClient.invalidateQueries({ queryKey: ['clients.profile', profile.id] });
        return;
      }
      toast.error(t('clientProfile.goToPreCall.errorTitle'), (err as Error).message);
    }
  };

  // Agent: own active assignment → "go to pre-call" (with preflight)
  if (currentUser.role === 'agent' && aa && aa.agentId === currentUser.id) {
    return (
      <Button size="sm" onClick={goPreCall}>
        {t('clientProfile.goToPreCall')}
      </Button>
    );
  }

  // Agent: foreign active assignment → "request reassign"
  if (currentUser.role === 'agent' && aa && aa.agentId !== currentUser.id) {
    return (
      <Button
        size="sm"
        variant="secondary"
        onClick={() => requestReassign.mutate()}
        disabled={requestReassign.isPending}
      >
        {t('clientProfile.requestReassign.action')}
      </Button>
    );
  }

  // Agent: no active assignment → S5. El miembro puede estar en la línea
  // AHORA: el botón principal lo atiende; el pedido al admin queda como
  // salida para cuando la reja del backend rechaza el reclamo.
  if (currentUser.role === 'agent' && !aa) {
    return (
      <div className="flex flex-col items-end gap-1.5">
        <div className="flex items-center gap-2">
          <Input
            type="tel"
            inputMode="tel"
            className="h-9 w-44"
            aria-label={t('clientProfile.claimInbound.phoneLabel')}
            placeholder={t('clientProfile.claimInbound.phonePlaceholder')}
            value={inboundPhone}
            onChange={(e) => setInboundPhone(e.target.value)}
            disabled={claimInbound.isPending}
          />
          <Button
            size="sm"
            onClick={() => claimInbound.mutate()}
            disabled={!telefonoUsable || claimInbound.isPending || requestReassign.isPending}
          >
            {t('clientProfile.claimInbound.action')}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => requestReassign.mutate()}
            disabled={requestReassign.isPending || claimInbound.isPending}
          >
            {t('clientProfile.requestAssignment.action')}
          </Button>
        </div>
        <p className="text-[11px] text-nav-slate italic m-0 text-right max-w-[22rem]">
          {t('clientProfile.claimInbound.hint')}
        </p>
      </div>
    );
  }

  // Admin: link to existing reassignment page (no new UI)
  if (currentUser.role === 'admin') {
    return (
      <Button asChild size="sm" variant="secondary">
        <Link to="/admin/reassign">
          {aa ? t('clientProfile.adminReassign') : t('clientProfile.adminAssign')}
        </Link>
      </Button>
    );
  }

  // Supervisor / uploader / unknown role: no action
  return null;
}
